import "core-js/modules/es.iterator.constructor.js";
import "core-js/modules/es.iterator.filter.js";
import "core-js/modules/es.iterator.map.js";
import "core-js/modules/es.set.difference.v2.js";
import "core-js/modules/es.set.intersection.v2.js";
import "core-js/modules/es.set.is-disjoint-from.v2.js";
import "core-js/modules/es.set.is-subset-of.v2.js";
import "core-js/modules/es.set.is-superset-of.v2.js";
import "core-js/modules/es.set.symmetric-difference.v2.js";
import "core-js/modules/es.set.union.v2.js";
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, toDisplayString as _toDisplayString } from "vue";
const _hoisted_1 = {
  class: "border-t border-slate-200 pt-5"
};
const _hoisted_2 = {
  class: "flex flex-wrap items-center justify-between gap-3"
};
const _hoisted_3 = {
  key: 0,
  class: "status-badge is-info"
};
const _hoisted_4 = {
  key: 0,
  class: "mt-4"
};
const _hoisted_5 = {
  key: 1,
  class: "rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-center text-sm text-slate-500"
};
const _hoisted_6 = {
  key: 1,
  class: "mt-4 rounded-lg border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500"
};
import { computed, ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import FileAttachment from '@/components/FileAttachment.vue';
import FileUpload from '@/components/FileUpload.vue';
import { deleteWorkReportAttachment, getWorkReportAttachmentDownloadUrl, registerWorkReportAttachment } from '@/api/workReport';
export default {
  __name: 'ReportAttachmentList',
  props: {
    reportId: {
      type: [Number, String],
      default: null
    },
    attachments: {
      type: Array,
      default: () => []
    },
    readonly: {
      type: Boolean,
      default: false
    },
    maxCount: {
      type: Number,
      default: 20
    },
    maxSizeMb: {
      type: Number,
      default: 20
    },
    allowedMediaTypes: {
      type: [Array, String],
      default: ''
    },
    allowedExtensions: {
      type: [Array, String],
      default: ''
    }
  },
  emits: ['update:attachments', 'changed'],
  setup(__props, {
    emit: __emit
  }) {
    const props = __props;
    const emit = __emit;
    const uploading = ref(false);
    const deletingId = ref(null);
    const localItems = ref([]);
    const uploadFiles = ref([]);
    const registeringUploads = ref(new Set());
    const registeringCount = ref(0);
    const items = computed(() => localItems.value);
    const previewFiles = computed(() => items.value.map(normalizeAttachmentForPreview));
    const uploadBusy = computed(() => uploading.value || registeringCount.value > 0);
    const maxCount = computed(() => Number(props.maxCount) > 0 ? Number(props.maxCount) : 20);
    const maxSizeMb = computed(() => Number(props.maxSizeMb) > 0 ? Number(props.maxSizeMb) : 20);
    const remainingUploadSlots = computed(() => Math.max(0, maxCount.value - items.value.length));
    const uploadAccept = computed(() => [...normalizeAcceptList(props.allowedMediaTypes), ...normalizeAcceptList(props.allowedExtensions).map(item => item.startsWith('.') || item.includes('/') ? item : `.${item}`)].filter(Boolean).join(','));
    watch(() => props.attachments, value => {
      localItems.value = Array.isArray(value) ? [...value] : [];
    }, {
      immediate: true,
      deep: true
    });
    async function handleUploadedFiles(files) {
      uploadFiles.value = Array.isArray(files) ? files : [];
      if (!props.reportId) return;
      for (const file of uploadFiles.value) {
        await registerUploadedFile(file);
      }
    }
    async function registerUploadedFile(file) {
      const uploadKey = uploadIdentity(file);
      if (!uploadKey || registeringUploads.value.has(uploadKey)) return;
      registeringUploads.value.add(uploadKey);
      registeringCount.value += 1;
      try {
        const uploaded = file;
        const objectKey = uploaded?.objectKey || uploaded?.key || uploaded?.fileKey || uploaded?.fileName;
        if (!objectKey) {
          ElMessage.error('上传结果缺少文件标识，请重试');
          return;
        }
        const attachment = await registerWorkReportAttachment(props.reportId, {
          objectKey,
          fileName: uploaded?.fileName || uploaded?.originalName || uploaded?.name,
          mediaType: uploaded?.mediaType || uploaded?.contentType || uploaded?.type || 'application/octet-stream',
          fileSize: uploaded?.fileSize || uploaded?.size
        });
        updateItems([...localItems.value, attachment]);
        uploadFiles.value = uploadFiles.value.filter(item => uploadIdentity(item) !== uploadKey);
        ElMessage.success('附件已上传');
      } catch {
        ElMessage.error('附件登记失败，请重试');
      } finally {
        registeringUploads.value.delete(uploadKey);
        registeringCount.value = Math.max(0, registeringCount.value - 1);
      }
    }
    function handleUploadingChange(value) {
      uploading.value = value;
    }
    async function loadAttachmentFile(file) {
      const attachmentId = attachmentIdentity(file);
      if (!attachmentId) throw new Error('附件标识缺失');
      return getWorkReportAttachmentDownloadUrl(props.reportId, attachmentId);
    }
    async function removeAttachment(file) {
      const attachmentId = attachmentIdentity(file);
      if (!attachmentId) return;
      deletingId.value = attachmentId;
      try {
        await deleteWorkReportAttachment(props.reportId, attachmentId);
        updateItems(localItems.value.filter(item => String(item.id) !== String(attachmentId)));
        ElMessage.success('附件已删除');
      } catch {
        ElMessage.error('附件删除失败，请重试');
      } finally {
        deletingId.value = null;
      }
    }
    function updateItems(nextItems) {
      localItems.value = nextItems;
      emit('update:attachments', nextItems);
      emit('changed', nextItems);
    }
    function normalizeAttachmentForPreview(attachment) {
      return {
        ...attachment,
        id: attachment.id,
        attachmentId: attachment.id,
        name: attachment.name || attachment.fileName || attachment.originalName,
        fileName: attachment.fileName || attachment.name || attachment.originalName,
        fileKey: attachment.objectKey || attachment.fileKey || String(attachment.id || ''),
        fileSize: attachment.fileSize || attachment.size,
        contentType: attachment.mediaType || attachment.contentType || attachment.type,
        previewMode: 'blob',
        raw: attachment
      };
    }
    function uploadIdentity(file) {
      return file?.uid || file?.objectKey || file?.key || file?.fileKey || file?.fileName || file?.name || '';
    }
    function attachmentIdentity(file) {
      if (!file || typeof file !== 'object') return null;
      return file.attachmentId ?? file.id ?? file.raw?.id ?? null;
    }
    function normalizeAcceptList(value) {
      if (Array.isArray(value)) return value.map(item => String(item).trim()).filter(Boolean);
      return String(value || '').split(',').map(item => item.trim()).filter(Boolean);
    }

    // FileUpload performs storage upload: uploadFile(file, 'work-report', 'private').

    return (_ctx, _cache) => {
      return _openBlock(), _createElementBlock("section", _hoisted_1, [_createElementVNode("div", _hoisted_2, [_cache[0] || (_cache[0] = _createElementVNode("div", null, [_createElementVNode("h3", {
        class: "text-sm font-semibold text-slate-900"
      }, "附件"), _createElementVNode("p", {
        class: "mt-1 text-xs text-slate-500"
      }, "支持拖拽上传和在线预览，附件会绑定到当前日报。")], -1)), uploadBusy.value ? (_openBlock(), _createElementBlock("span", _hoisted_3, "处理中")) : _createCommentVNode("", true)]), !__props.readonly ? (_openBlock(), _createElementBlock("div", _hoisted_4, [__props.reportId && remainingUploadSlots.value > 0 ? (_openBlock(), _createBlock(FileUpload, {
        key: 0,
        files: uploadFiles.value,
        module: "work-report",
        bucket: "private",
        "max-count": remainingUploadSlots.value,
        "max-size": maxSizeMb.value,
        accept: uploadAccept.value,
        "onUpdate:files": handleUploadedFiles,
        onUploadingChange: handleUploadingChange
      }, null, 8, ["files", "max-count", "max-size", "accept"])) : (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(__props.reportId ? '附件数量已达上限' : '日报生成后可上传附件'), 1))])) : _createCommentVNode("", true), items.value.length === 0 ? (_openBlock(), _createElementBlock("div", _hoisted_6, "暂无附件")) : (_openBlock(), _createBlock(FileAttachment, {
        key: 2,
        class: "mt-4",
        files: previewFiles.value,
        loader: loadAttachmentFile,
        removable: !__props.readonly,
        "removing-id": deletingId.value,
        onRemove: removeAttachment
      }, null, 8, ["files", "removable", "removing-id"]))]);
    };
  }
};